import { useState } from 'react'

import { Divider, Heading, Text } from '@hhgtech/hhg-components/components'
import { NavMenuContentType } from '@hhgtech/hhg-components/components/navigationV2/index'
import { theme } from '@hhgtech/hhg-components/misc'
import { useIntl } from 'react-intl'

import { Container } from 'components/atoms/container'
import { ExpertRelativeArticleCard } from 'components/molecules/expertRelativeArticleCard'
import { HealthToolBreadcrumb } from 'components/molecules/healthToolBreadcrumb'
import { TableOfContent } from 'components/molecules/tableOfContent'
import MobileBottomNavigationWrapper from 'components/organisms/mobileBottomNavigationWrapper'
import { NavigatorLayout } from 'components/templates/navigatorLayout'
import { useIsMobile } from 'hooks/useIsMobile'
import {
  assetImagePath,
  assetSvgPath,
  DEFAULT_AVATAR_IMG,
} from 'interfaces/constants'
import { ExpertUser, FooterInfo } from 'interfaces/types'
// import { deviceLayout } from 'styles/breakpoint'

import {
  StyledAuthorDetail,
  StyledAuthorPage,
  StyledDotSeparator,
  StyledInfoContainer,
  StyledItem,
  StyledListItems,
  StyledListPapers,
  StyledPapersHeading,
  StyledProfileImage,
  StyledReadmorePolicy,
  StyledSocialIcon,
  StyledSocicalIconContainer,
  StyledTitleContainer,
} from './index.styled'

export type Props = {
  type: 'expert' | 'author'
  expert: ExpertUser
  isMobileSsr: boolean
  footerInfo?: FooterInfo
  dataNavMenuContent?: NavMenuContentType
}

const _Author = ({ type, expert, footerInfo, dataNavMenuContent }: Props) => {
  const [openPublishPaper, setOpenPublishPaper] = useState(false)
  const isMobile = useIsMobile()
  const { formatMessage: f } = useIntl()

  const [showAllEducation, setShowAllEducation] = useState(false)
  const [showAllExperiences, setShowAllExperiences] = useState(false)
  const [showAllAwards, setShowAllAwards] = useState(false)

  return (
    <NavigatorLayout
      SEOData={{
        apiMetaData: expert.metaTag,
        isIndexable: true,
      }}
      type="profile"
      footerInfo={footerInfo}
      dataNavMenuContent={dataNavMenuContent}
    >
      {isMobile ? (
        <Container>
          <HealthToolBreadcrumb
            pageItem={[
              ...(type === 'expert'
                ? [
                    {
                      label: f({
                        id: 'experts.allExperts',
                        defaultMessage: 'Experts',
                      }),
                      value: `/expert`,
                    },
                  ]
                : []),
              {
                label: expert.displayName,
                value: expert.profileUrl,
              },
            ]}
          />

          <StyledAuthorPage>
            <StyledInfoContainer>
              <StyledProfileImage>
                <div className="image-container">
                  <div style={{ position: 'relative' }}>
                    <img
                      src={
                        expert.avatarThumbnail ||
                        expert.avatar ||
                        DEFAULT_AVATAR_IMG
                      }
                      className="profile-image"
                      alt={'avatar'}
                      onError={(e) =>
                        (e.currentTarget.src = DEFAULT_AVATAR_IMG)
                      }
                      loading="lazy"
                    />
                    {expert?.role && (
                      <img
                        className="badge-avatar"
                        src={`${assetImagePath}/reviewer-${
                          expert.role || ''
                        }.svg`}
                        alt="badge"
                        loading="lazy"
                      />
                    )}
                  </div>
                </div>
              </StyledProfileImage>
              <StyledTitleContainer>
                <Heading tag="h3">{expert.displayName}</Heading>
                <TableOfContent
                  size="md"
                  contents={[
                    ...(expert?.fields.specialty
                      ? [
                          <Text
                            key={'1'}
                            size="p3"
                            color={theme.colors.gray800}
                          >
                            {expert?.fields.specialty}
                          </Text>,
                        ]
                      : []),
                    ...(expert?.fields.organisation
                      ? [
                          <Text
                            key={'2'}
                            size="p3"
                            className="organisation"
                            color={theme.colors.gray500}
                          >
                            <a
                              href={expert?.organisation_url || undefined}
                              className="organisation"
                              target="_blank"
                            >
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: expert?.fields.organisation,
                                }}
                              ></span>
                            </a>
                          </Text>,
                        ]
                      : []),
                  ]}
                  separator={<StyledDotSeparator />}
                  className="position-container"
                />
                <StyledSocicalIconContainer>
                  {Object.keys(expert?.fields?.contact).map(
                    (key: string, index) => {
                      if (
                        !expert?.fields?.contact?.[
                          key as
                            | 'url'
                            | 'facebook'
                            | 'instagram'
                            | 'linkedin'
                            | 'myspace'
                            | 'pinterest'
                            | 'soundcloud'
                            | 'tumblr'
                            | 'twitter'
                            | 'youtube'
                            | 'wikipedia'
                            | 'tiktok'
                        ]
                      )
                        return null
                      return (
                        <StyledSocialIcon
                          key={index}
                          href={
                            expert?.fields?.contact?.[
                              key as
                                | 'url'
                                | 'facebook'
                                | 'instagram'
                                | 'linkedin'
                                | 'myspace'
                                | 'pinterest'
                                | 'soundcloud'
                                | 'tumblr'
                                | 'twitter'
                                | 'youtube'
                                | 'wikipedia'
                                | 'tiktok'
                            ] || ''
                          }
                          className="no-replace-click"
                          target="_blank"
                        >
                          <img
                            src={`${assetSvgPath}/${key}.svg`}
                            className="social-icons"
                            alt=""
                            loading="lazy"
                          />
                        </StyledSocialIcon>
                      )
                    },
                  )}
                </StyledSocicalIconContainer>
              </StyledTitleContainer>
            </StyledInfoContainer>
            {!!expert.description && (
              <>
                <StyledAuthorDetail>
                  {type === 'author' ? (
                    <Heading tag="h3">
                      {f({
                        id: 'author.content.about',
                        defaultMessage: 'About',
                      })}
                    </Heading>
                  ) : (
                    <Heading tag="h3">
                      {f({
                        id: 'expert.content.about',
                        defaultMessage: 'About',
                      })}
                    </Heading>
                  )}
                  <Text size="p1">
                    <div
                      className="expert-description"
                      dangerouslySetInnerHTML={{ __html: expert.description }}
                    ></div>
                  </Text>
                </StyledAuthorDetail>
                <Divider type="solid" className="divider" />
              </>
            )}

            <StyledAuthorDetail>
              {expert?.fields?.educations ||
              expert?.fields?.experiences ||
              expert?.fields?.awards_accreditations ? (
                <Heading tag="h3">
                  {f({
                    id: 'expert.content.highlight',
                    defaultMessage: 'Credentials & Highlights',
                  })}
                </Heading>
              ) : null}
              {expert?.fields?.educations &&
                Array.isArray(expert?.fields?.educations) &&
                expert?.fields?.educations.length > 0 && (
                  <>
                    <Text
                      type="bold"
                      size="c2"
                      color={theme.colors.gray400}
                      style={{ marginTop: 16 }}
                    >
                      {f({
                        id: 'expert.content.education',
                        defaultMessage: 'Education',
                      }).toUpperCase()}
                    </Text>
                    <StyledListItems>
                      {(showAllEducation
                        ? expert?.fields?.educations
                        : expert?.fields?.educations.slice(0, 3)
                      ).map((education, index) => (
                        <StyledItem key={`item-${index}`}>
                          <img
                            className="bullet-point"
                            src={`${assetImagePath}/bullet-point.svg`}
                            alt="point"
                            loading="lazy"
                          />
                          <div className="text-contain">
                            <Heading tag="h6" color={theme.colors.gray800}>
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: education.study,
                                }}
                              />
                            </Heading>
                            <Text
                              size="p3"
                              color={theme.colors.gray500}
                              dangerouslySetInnerHTML={{
                                __html: education?.school_name || '',
                              }}
                            />
                          </div>
                        </StyledItem>
                      ))}
                    </StyledListItems>
                    {expert?.fields?.educations.length > 3 &&
                    !showAllEducation ? (
                      <div
                        className="see-more"
                        onClick={() => setShowAllEducation(true)}
                      >
                        {f({
                          id: 'expert.content.seeMore',
                          defaultMessage: 'See more',
                        })}
                        <img
                          src={`${assetImagePath}/chevronDownBlue.svg`}
                          alt="see-more"
                          loading="lazy"
                        />
                      </div>
                    ) : null}
                    <Divider type="solid" className="list-divider" />
                  </>
                )}

              {type === 'expert' &&
                expert?.fields?.experiences &&
                Array.isArray(expert?.fields?.experiences) &&
                expert?.fields?.experiences.length > 0 && (
                  <>
                    <Text
                      type="bold"
                      size="c2"
                      color={theme.colors.gray400}
                      style={{ marginTop: 16 }}
                    >
                      {f({
                        id: 'expert.content.experiences',
                        defaultMessage: 'Experiences',
                      }).toUpperCase()}
                    </Text>

                    <StyledListItems>
                      {(showAllExperiences
                        ? expert?.fields?.experiences
                        : expert?.fields?.experiences.slice(0, 3)
                      ).map((experience, index) => (
                        <StyledItem key={`item-${index}`}>
                          <img
                            className="bullet-point"
                            src={`${assetImagePath}/bullet-point.svg`}
                            alt="point"
                            loading="lazy"
                          />
                          <div className="text-contain">
                            <Heading tag="h6" color={theme.colors.gray800}>
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: experience?.title || '',
                                }}
                              />
                            </Heading>
                            <Text
                              size="p3"
                              color={theme.colors.gray500}
                              dangerouslySetInnerHTML={{
                                __html: experience?.organization || '',
                              }}
                            />
                          </div>
                        </StyledItem>
                      ))}
                    </StyledListItems>

                    {expert?.fields?.experiences.length > 3 &&
                    !showAllExperiences ? (
                      <div
                        className="see-more"
                        onClick={() => setShowAllExperiences(true)}
                      >
                        {f({
                          id: 'expert.content.seeMore',
                          defaultMessage: 'See more',
                        })}
                        <img
                          src={`${assetImagePath}/chevronDownBlue.svg`}
                          alt="see-more"
                          loading="lazy"
                        />
                      </div>
                    ) : null}
                    <Divider type="solid" className="list-divider" />
                  </>
                )}

              {type === 'expert' &&
                expert?.fields?.awards_accreditations &&
                Array.isArray(expert?.fields?.awards_accreditations) &&
                expert?.fields?.awards_accreditations.length > 0 && (
                  <>
                    <Text
                      type="bold"
                      size="c2"
                      color={theme.colors.gray400}
                      style={{ marginTop: 16 }}
                    >
                      {f({
                        id: 'expert.content.awards',
                        defaultMessage: 'Awards & Accreditations',
                      }).toUpperCase()}
                    </Text>
                    <StyledListItems>
                      {(showAllAwards
                        ? expert?.fields?.awards_accreditations
                        : expert?.fields?.awards_accreditations.slice(0, 3)
                      ).map((awards_accreditation, index) => (
                        <StyledItem key={`item-${index}`}>
                          <img
                            className="bullet-point"
                            src={`${assetImagePath}/bullet-point.svg`}
                            alt="point"
                            loading="lazy"
                          />
                          <div className="text-contain">
                            <Text size="p3" color={theme.colors.gray800}>
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: awards_accreditation.title,
                                }}
                              />
                            </Text>
                          </div>
                        </StyledItem>
                      ))}
                    </StyledListItems>
                    {expert?.fields?.awards_accreditations.length > 3 &&
                    !showAllAwards ? (
                      <div
                        className="see-more"
                        onClick={() => setShowAllAwards(true)}
                      >
                        {f({
                          id: 'expert.content.seeMore',
                          defaultMessage: 'See more',
                        })}
                        <img
                          src={`${assetImagePath}/chevronDownBlue.svg`}
                          alt="see-more"
                          loading="lazy"
                        />
                      </div>
                    ) : null}
                    <Divider type="solid" className="list-divider" />
                  </>
                )}

              {expert?.fields?.sources &&
                Array.isArray(expert?.fields?.sources) &&
                expert?.fields?.sources.length > 0 && (
                  <>
                    <StyledPapersHeading
                      onClick={() => setOpenPublishPaper((v) => !v)}
                    >
                      <Text
                        type="bold"
                        size="c2"
                        color={theme.colors.gray500}
                        style={{ marginTop: 16 }}
                      >
                        <img
                          className="heading-icons"
                          src={`${assetSvgPath}/${
                            openPublishPaper ? 'minus' : 'plus'
                          }.svg`}
                          alt="plus-icon"
                          loading="lazy"
                        />
                        {f(
                          {
                            id: 'expert.content.publishedPapers',
                            defaultMessage: '{number} Published papers',
                          },
                          {
                            number: expert?.fields?.sources.length,
                          },
                        ).toUpperCase()}
                      </Text>
                    </StyledPapersHeading>
                    <StyledListPapers data-is-open={openPublishPaper}>
                      <div className="link-container">
                        {expert?.fields?.sources.map((source, index) => (
                          <div
                            className="paper-link"
                            data-is-url-disabled={!!!source.url}
                            key={index}
                          >
                            <a href={source.url} target="_blank">
                              {source.title || source.url}
                            </a>
                          </div>
                        ))}
                      </div>
                    </StyledListPapers>
                  </>
                )}

              {/* <StyledAuthorDetail>
                {Object.values(expert?.fields?.contact).every(
                  (x) => x === null || x === '',
                ) ? null : (
                  <>
                    <Heading
                      tag="h3"
                      style={{ marginBottom: 20, marginTop: 80 }}
                    >
                      {f({
                        id: 'expert.content.sociaLinks',
                        defaultMessage: 'Social Links',
                      })}
                    </Heading>
                    {Object.keys(expert?.fields?.contact).map(
                      (key: string, index) => {
                        if (
                          !expert?.fields?.contact?.[
                            key as
                              | 'url'
                              | 'facebook'
                              | 'instagram'
                              | 'linkedin'
                              | 'myspace'
                              | 'pinterest'
                              | 'soundcloud'
                              | 'tumblr'
                              | 'twitter'
                              | 'youtube'
                              | 'wikipedia'
                          ]
                        )
                          return null
                        return (
                          <React.Fragment key={index}>
                            <StyledSocialLinkContainer>
                              <img
                                src={`${assetSvgPath}/${key}.svg`}
                                className="social-icons"
                              />
                              <div className="info-container">
                                <Heading tag="h6" color={theme.colors.gray800}>
                                  {key}
                                </Heading>
                                <a
                                  href={
                                    expert?.fields?.contact?.[
                                      key as
                                        | 'url'
                                        | 'facebook'
                                        | 'instagram'
                                        | 'linkedin'
                                        | 'myspace'
                                        | 'pinterest'
                                        | 'soundcloud'
                                        | 'tumblr'
                                        | 'twitter'
                                        | 'youtube'
                                        | 'wikipedia'
                                    ] || ''
                                  }
                                  className="link-text no-replace-click"
                                  target="_blank"
                                >
                                  {expert?.fields?.contact?.[
                                    key as
                                      | 'url'
                                      | 'facebook'
                                      | 'instagram'
                                      | 'linkedin'
                                      | 'myspace'
                                      | 'pinterest'
                                      | 'soundcloud'
                                      | 'tumblr'
                                      | 'twitter'
                                      | 'youtube'
                                      | 'wikipedia'
                                  ]?.replace('https://www.', '')}
                                </a>
                              </div>
                            </StyledSocialLinkContainer>
                            <Divider type="solid" className="list-divider" />
                          </React.Fragment>
                        )
                      },
                    )}
                  </>
                )}
              </StyledAuthorDetail> */}

              {expert?.relatedPost &&
              Array.isArray(expert?.relatedPost) &&
              expert?.relatedPost.length > 0 ? (
                <>
                  <Divider type="solid" className="divider" />
                  <StyledAuthorDetail>
                    <Heading tag="h3">
                      {f({
                        id: 'expert.content.latestArticles',
                        defaultMessage: 'Latest Expert Articles',
                      })}
                    </Heading>
                    <ExpertRelativeArticleCard
                      isMobile={isMobile}
                      trendingPosts={expert.relatedPost}
                    />
                  </StyledAuthorDetail>
                </>
              ) : null}

              {expert.fields.policies ? (
                <>
                  <Divider type="solid" className="divider" />
                  <StyledAuthorDetail>
                    <Heading tag="h3" style={{ marginBottom: 20 }}>
                      {expert.fields.policies?.title}
                    </Heading>
                    <Text size="p1">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: expert.fields.policies.content,
                        }}
                      ></div>
                    </Text>
                    {expert.fields.policies?.readmore ? (
                      <StyledReadmorePolicy
                        href={expert.fields.policies.readmore_url || ''}
                      >
                        <Heading
                          fontWeight="bold"
                          tag="h6"
                          color={theme.colors.primaryActive}
                        >
                          {expert.fields.policies.readmore}
                        </Heading>
                        <img
                          src={`${assetImagePath}/chevronRightBlue.svg`}
                          alt="right-chevron"
                          loading="lazy"
                        />
                      </StyledReadmorePolicy>
                    ) : null}
                  </StyledAuthorDetail>
                </>
              ) : null}
            </StyledAuthorDetail>

            <MobileBottomNavigationWrapper />
          </StyledAuthorPage>
        </Container>
      ) : (
        <>
          <Container>
            <HealthToolBreadcrumb
              pageItem={[
                ...(type === 'expert'
                  ? [
                      {
                        label: f({
                          id: 'experts.allExperts',
                          defaultMessage: 'Experts',
                        }),
                        value: `/expert`,
                      },
                    ]
                  : []),
                {
                  label: expert.displayName,
                  value: expert.profileUrl,
                },
              ]}
            />
          </Container>
          <StyledAuthorPage>
            <StyledInfoContainer>
              <div className="avatar-title">
                <StyledProfileImage>
                  <div className="image-container">
                    <div style={{ position: 'relative' }}>
                      <img
                        src={
                          expert.avatar ||
                          expert.avatarThumbnail ||
                          DEFAULT_AVATAR_IMG
                        }
                        style={{
                          width: '168px',
                          height: '168px',
                        }}
                        className="profile-image"
                        loading="lazy"
                        alt={'avatar'}
                        onError={(e) =>
                          (e.currentTarget.src = DEFAULT_AVATAR_IMG)
                        }
                      />
                      {expert?.role && (
                        <img
                          className="badge-avatar"
                          src={`${assetImagePath}/reviewer-${
                            expert.role || ''
                          }.svg`}
                          alt="badge"
                          loading="lazy"
                        />
                      )}
                    </div>
                  </div>
                </StyledProfileImage>
                <StyledTitleContainer>
                  <Heading tag="h3">{expert.displayName}</Heading>
                  <TableOfContent
                    size="md"
                    contents={[
                      ...(expert?.fields.specialty
                        ? [
                            <Text
                              key={'1'}
                              size="p3"
                              color={theme.colors.gray800}
                            >
                              {expert?.fields.specialty}
                            </Text>,
                          ]
                        : []),
                      ...(expert?.fields.organisation
                        ? [
                            <Text
                              key={'2'}
                              size="p3"
                              color={theme.colors.gray500}
                            >
                              <a
                                href={expert?.organisation_url || undefined}
                                className="organisation"
                                target="_blank"
                              >
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: expert?.fields.organisation,
                                  }}
                                ></span>
                              </a>
                            </Text>,
                          ]
                        : []),
                    ]}
                    separator={<StyledDotSeparator />}
                    className="position-container"
                  />
                  <StyledSocicalIconContainer>
                    {Object.keys(expert?.fields?.contact).map(
                      (key: string, index) => {
                        if (
                          !expert?.fields?.contact?.[
                            key as
                              | 'url'
                              | 'facebook'
                              | 'instagram'
                              | 'linkedin'
                              | 'myspace'
                              | 'pinterest'
                              | 'soundcloud'
                              | 'tumblr'
                              | 'twitter'
                              | 'youtube'
                              | 'wikipedia'
                              | 'tiktok'
                          ]
                        )
                          return null
                        return (
                          <StyledSocialIcon
                            key={index}
                            href={
                              expert?.fields?.contact?.[
                                key as
                                  | 'url'
                                  | 'facebook'
                                  | 'instagram'
                                  | 'linkedin'
                                  | 'myspace'
                                  | 'pinterest'
                                  | 'soundcloud'
                                  | 'tumblr'
                                  | 'twitter'
                                  | 'youtube'
                                  | 'wikipedia'
                                  | 'tiktok'
                              ] || ''
                            }
                            className="no-replace-click"
                            target="_blank"
                          >
                            <img
                              src={`${assetSvgPath}/${key}.svg`}
                              className="social-icons"
                              alt=""
                              loading="lazy"
                            />
                          </StyledSocialIcon>
                        )
                      },
                    )}
                  </StyledSocicalIconContainer>
                </StyledTitleContainer>
              </div>

              <div className="border-banner"></div>
            </StyledInfoContainer>
            <Container>
              {expert.description ? (
                <>
                  <StyledAuthorDetail>
                    {type === 'author' ? (
                      <Heading tag="h3">
                        {f({
                          id: 'author.content.about',
                          defaultMessage: 'About',
                        })}
                      </Heading>
                    ) : (
                      <Heading tag="h3">
                        {f({
                          id: 'expert.content.about',
                          defaultMessage: 'About',
                        })}
                      </Heading>
                    )}
                    <Text size="p1">
                      <div
                        className="expert-description"
                        dangerouslySetInnerHTML={{ __html: expert.description }}
                      ></div>
                    </Text>
                  </StyledAuthorDetail>
                </>
              ) : null}

              <StyledAuthorDetail>
                {expert?.fields?.educations ||
                expert?.fields?.experiences ||
                expert?.fields?.awards_accreditations ? (
                  <Heading tag="h3">
                    {f({
                      id: 'expert.content.highlight',
                      defaultMessage: 'Credentials & Highlights',
                    })}
                  </Heading>
                ) : null}
                {expert?.fields?.educations ||
                expert?.fields?.experiences ||
                expert?.fields?.awards_accreditations ? (
                  <>
                    <div className="highlight-wrapper">
                      {expert?.fields?.educations &&
                        Array.isArray(expert?.fields?.educations) &&
                        expert?.fields?.educations.length > 0 && (
                          <div className="highlight-item">
                            <Text
                              type="bold"
                              size="c2"
                              color={theme.colors.gray400}
                              style={{ marginTop: 16 }}
                            >
                              {f({
                                id: 'expert.content.education',
                                defaultMessage: 'Education',
                              }).toUpperCase()}
                            </Text>
                            <StyledListItems>
                              {(showAllEducation
                                ? expert?.fields?.educations
                                : expert?.fields?.educations.slice(0, 3)
                              ).map((education, index) => (
                                <StyledItem key={`item-${index}`}>
                                  <img
                                    className="bullet-point"
                                    src={`${assetImagePath}/bullet-point.svg`}
                                    alt="point"
                                    loading="lazy"
                                  />
                                  <div className="text-contain">
                                    <Heading
                                      tag="h6"
                                      color={theme.colors.gray800}
                                    >
                                      <span
                                        dangerouslySetInnerHTML={{
                                          __html: education.study,
                                        }}
                                      />
                                    </Heading>
                                    <Text
                                      size="p3"
                                      color={theme.colors.gray500}
                                      dangerouslySetInnerHTML={{
                                        __html: education?.school_name || '',
                                      }}
                                    />
                                  </div>
                                </StyledItem>
                              ))}
                            </StyledListItems>
                            {expert?.fields?.educations.length > 3 &&
                            !showAllEducation ? (
                              <div
                                className="see-more"
                                onClick={() => setShowAllEducation(true)}
                              >
                                {f({
                                  id: 'expert.content.seeMore',
                                  defaultMessage: 'See more',
                                })}
                                <img
                                  src={`${assetImagePath}/chevronDownBlue.svg`}
                                  alt="see-more"
                                  loading="lazy"
                                />
                              </div>
                            ) : null}
                          </div>
                        )}

                      {type === 'expert' &&
                        expert?.fields?.experiences &&
                        Array.isArray(expert?.fields?.experiences) &&
                        expert?.fields?.experiences.length > 0 && (
                          <div className="highlight-item">
                            <Text
                              type="bold"
                              size="c2"
                              color={theme.colors.gray400}
                              style={{ marginTop: 16 }}
                            >
                              {f({
                                id: 'expert.content.experiences',
                                defaultMessage: 'Experiences',
                              }).toUpperCase()}
                            </Text>
                            <StyledListItems>
                              {(showAllExperiences
                                ? expert?.fields?.experiences
                                : expert?.fields?.experiences.slice(0, 3)
                              ).map((experience, index) => (
                                <StyledItem key={`item-${index}`}>
                                  <img
                                    className="bullet-point"
                                    src={`${assetImagePath}/bullet-point.svg`}
                                    alt="point"
                                    loading="lazy"
                                  />
                                  <div className="text-contain">
                                    <Heading
                                      tag="h6"
                                      color={theme.colors.gray800}
                                    >
                                      <span
                                        dangerouslySetInnerHTML={{
                                          __html: experience.title,
                                        }}
                                      />
                                    </Heading>
                                    <Text
                                      size="p3"
                                      color={theme.colors.gray500}
                                      dangerouslySetInnerHTML={{
                                        __html: experience?.organization || '',
                                      }}
                                    />
                                  </div>
                                </StyledItem>
                              ))}
                            </StyledListItems>
                            {expert?.fields?.experiences.length > 3 &&
                            !showAllExperiences ? (
                              <div
                                className="see-more"
                                onClick={() => setShowAllExperiences(true)}
                              >
                                {f({
                                  id: 'expert.content.seeMore',
                                  defaultMessage: 'See more',
                                })}
                                <img
                                  src={`${assetImagePath}/chevronDownBlue.svg`}
                                  alt="see-more"
                                  loading="lazy"
                                />
                              </div>
                            ) : null}
                          </div>
                        )}

                      {type === 'expert' &&
                        expert?.fields?.awards_accreditations &&
                        Array.isArray(expert?.fields?.awards_accreditations) &&
                        expert?.fields?.awards_accreditations.length > 0 && (
                          <div className="highlight-item">
                            <Text
                              type="bold"
                              size="c2"
                              color={theme.colors.gray400}
                              style={{ marginTop: 16 }}
                            >
                              {f({
                                id: 'expert.content.awards',
                                defaultMessage: 'Awards & Accreditations',
                              }).toUpperCase()}
                            </Text>
                            <StyledListItems>
                              {(showAllAwards
                                ? expert?.fields?.awards_accreditations
                                : expert?.fields?.awards_accreditations.slice(
                                    0,
                                    3,
                                  )
                              ).map((awards_accreditation, index) => (
                                <StyledItem key={`item-${index}`}>
                                  <img
                                    className="bullet-point"
                                    src={`${assetImagePath}/bullet-point.svg`}
                                    alt="point"
                                    loading="lazy"
                                  />
                                  <div className="text-contain">
                                    <Text
                                      size="p3"
                                      color={theme.colors.gray800}
                                      dangerouslySetInnerHTML={{
                                        __html: awards_accreditation?.title,
                                      }}
                                    />
                                  </div>
                                </StyledItem>
                              ))}
                            </StyledListItems>

                            {expert?.fields?.awards_accreditations.length > 3 &&
                            !showAllAwards ? (
                              <div
                                className="see-more"
                                onClick={() => setShowAllAwards(true)}
                              >
                                {f({
                                  id: 'expert.content.seeMore',
                                  defaultMessage: 'See more',
                                })}
                                <img
                                  src={`${assetImagePath}/chevronDownBlue.svg`}
                                  alt="see-more"
                                  loading="lazy"
                                />
                              </div>
                            ) : null}
                          </div>
                        )}
                    </div>
                    <Divider type="solid" className="list-divider" />
                  </>
                ) : null}
                {expert?.fields?.sources &&
                  Array.isArray(expert?.fields?.sources) &&
                  expert?.fields?.sources.length > 0 && (
                    <>
                      <StyledPapersHeading
                        onClick={() => setOpenPublishPaper((v) => !v)}
                      >
                        <Text
                          type="bold"
                          size="c2"
                          color={theme.colors.gray500}
                          style={{
                            marginTop: 16,
                            marginBottom: 8,
                            cursor: 'pointer',
                          }}
                        >
                          <img
                            className="heading-icons"
                            src={`${assetSvgPath}/${
                              openPublishPaper ? 'minus' : 'plus'
                            }.svg`}
                            alt="plus-icon"
                            loading="lazy"
                          />

                          {f(
                            {
                              id: 'expert.content.publishedPapers',
                              defaultMessage: '{number} Published papers',
                            },
                            {
                              number: expert?.fields?.sources.length,
                            },
                          ).toUpperCase()}
                        </Text>
                      </StyledPapersHeading>
                      <StyledListPapers data-is-open={openPublishPaper}>
                        <div className="link-container">
                          {expert?.fields?.sources.map((source, index) => (
                            <div
                              className="paper-link"
                              data-is-url-disabled={!!!source.url}
                              key={index}
                            >
                              <a href={source.url} target="_blank">
                                {source.title || source.url}
                              </a>
                            </div>
                          ))}
                        </div>
                      </StyledListPapers>
                      <Divider type="solid" className="list-divider" />
                    </>
                  )}
                {/* {Object.values(expert?.fields?.contact).every(
                  (x) => x === null || x === '',
                ) ? null : (
                  <>
                    <Heading
                      tag="h3"
                      style={{ marginBottom: 20, marginTop: 80 }}
                    >
                      {f({
                        id: 'expert.content.sociaLinks',
                        defaultMessage: 'Social Links',
                      })}
                    </Heading>
                    <div className="social-row">
                      {Object.keys(expert?.fields?.contact).map(
                        (key: string, index) => {
                          if (
                            !expert?.fields?.contact?.[
                              key as
                                | 'url'
                                | 'facebook'
                                | 'instagram'
                                | 'linkedin'
                                | 'myspace'
                                | 'pinterest'
                                | 'soundcloud'
                                | 'tumblr'
                                | 'twitter'
                                | 'youtube'
                                | 'wikipedia'
                            ]
                          )
                            return null
                          return (
                            <StyledSocialLinkContainer key={index}>
                              <img
                                src={`${assetSvgPath}/${key}.svg`}
                                className="social-icons"
                              />
                              <div className="info-container">
                                <Heading tag="h6" color={theme.colors.gray800}>
                                  {key}
                                </Heading>
                                <Text size="p4">
                                  <a
                                    href={
                                      expert?.fields?.contact?.[
                                        key as
                                          | 'url'
                                          | 'facebook'
                                          | 'instagram'
                                          | 'linkedin'
                                          | 'myspace'
                                          | 'pinterest'
                                          | 'soundcloud'
                                          | 'tumblr'
                                          | 'twitter'
                                          | 'youtube'
                                          | 'wikipedia'
                                      ] || ''
                                    }
                                    className="link-text no-replace-click"
                                    target="_blank"
                                  >
                                    {expert?.fields?.contact?.[
                                      key as
                                        | 'url'
                                        | 'facebook'
                                        | 'instagram'
                                        | 'linkedin'
                                        | 'myspace'
                                        | 'pinterest'
                                        | 'soundcloud'
                                        | 'tumblr'
                                        | 'twitter'
                                        | 'youtube'
                                        | 'wikipedia'
                                    ]?.replace('https://www.', '')}
                                  </a>
                                </Text>
                              </div>
                            </StyledSocialLinkContainer>
                          )
                        },
                      )}
                    </div>
                  </>
                )} */}

                {expert?.relatedPost &&
                Array.isArray(expert?.relatedPost) &&
                expert?.relatedPost.length > 0 ? (
                  <>
                    <Heading tag="h3" style={{ marginTop: 80 }}>
                      {f({
                        id: 'expert.content.latestArticles',
                        defaultMessage: 'Latest Expert Articles',
                      })}
                    </Heading>
                    <ExpertRelativeArticleCard
                      isMobile={isMobile}
                      trendingPosts={expert.relatedPost}
                    />
                  </>
                ) : null}

                {expert.fields.policies ? (
                  <>
                    <Heading
                      tag="h3"
                      style={{ marginBottom: 20, marginTop: 80 }}
                    >
                      {expert.fields.policies?.title}
                    </Heading>
                    <Text size="p1">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: expert.fields.policies.content,
                        }}
                      ></div>
                    </Text>
                    {expert.fields.policies?.readmore ? (
                      <StyledReadmorePolicy
                        href={expert.fields.policies.readmore_url || ''}
                      >
                        <Heading
                          fontWeight="bold"
                          tag="h6"
                          as="h6"
                          color={theme.colors.primaryActive}
                        >
                          {expert.fields.policies.readmore}
                        </Heading>
                        <img
                          src={`${assetImagePath}/chevronRightBlue.svg`}
                          alt="right-chevron"
                          loading="lazy"
                        />
                      </StyledReadmorePolicy>
                    ) : null}
                  </>
                ) : null}
              </StyledAuthorDetail>
            </Container>
          </StyledAuthorPage>
        </>
      )}
    </NavigatorLayout>
  )
}

export default _Author
